import React, { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Reveal } from './Reveal';

const FavoritesSection = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <section
      id="favorites"
      ref={sectionRef}
      className="relative py-28 md:py-40 px-6 md:px-12 bg-background overflow-hidden"
      data-testid="favorites-section"
    >
      <div className="max-w-[1480px] mx-auto">
        {/* Heading row */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
              {language === 'hu' ? '04 — A Kedvenceim' : '04 — The Favorites'}
            </Reveal>
            <Reveal as="h2" duration={1.1} className="font-heading text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight">
              <span className="block font-light">{language === 'hu' ? 'Amit' : 'What I'}</span>
              <span className="block italic font-extralight text-text-secondary">
                {language === 'hu' ? 'minden nap használok.' : 'use every day.'}
              </span>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <Reveal as="p" delay={0.15} className="font-heading text-2xl md:text-3xl italic text-text-secondary leading-snug max-w-md">
              {t.favorites.subtitle}
            </Reveal>
          </div>
        </div>

        {/* Asymmetric layout */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-stretch">
          {/* Image — large */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              style={{ y: imgY }}
              className="group relative aspect-[5/4] md:aspect-[4/3] rounded-3xl overflow-hidden grain-layer shadow-editorial"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.9, ease: [0.16,1,0.3,1] }}
            >
              <motion.img
                src="https://images.pexels.com/photos/7670759/pexels-photo-7670759.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200"
                alt="Favorite Products"
                className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                data-testid="favorites-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-text-primary/15 via-transparent to-transparent" />

              {/* Floating tag */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 1, ease: [0.16,1,0.3,1] }}
                className="absolute bottom-8 left-8 flex items-center gap-3 bg-background/85 backdrop-blur-md border border-border/70 px-5 py-3 rounded-full"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-primary">
                  {language === 'hu' ? 'Amazon wishlist' : 'Amazon wishlist'}
                </span>
              </motion.div>
            </motion.div>
          </div>

          {/* Editorial right column */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col justify-between">
            <Reveal as="p" delay={0.15} className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-12 lg:mb-0 max-w-md">
              {t.favorites.description}
            </Reveal>

            {/* Small thumbs row (decorative cards) */}
            <div className="grid grid-cols-2 gap-5 my-10">
              {[
                { label: language === 'hu' ? 'Bőrápolás' : 'Skincare', tone: 'bg-surface-highlight' },
                { label: language === 'hu' ? 'Wellness' : 'Wellness', tone: 'bg-gradient-to-br from-[#E8D6B0] to-[#C5A059]' },
                { label: language === 'hu' ? 'Otthon' : 'Home', tone: 'bg-surface' },
                { label: language === 'hu' ? 'Életstílus' : 'Lifestyle', tone: 'bg-text-primary text-background' },
              ].map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.08 * i, duration: 0.9, ease: [0.16,1,0.3,1] }}
                  className={`relative h-28 rounded-2xl border border-border/60 ${c.tone} grain-layer overflow-hidden flex items-end p-4`}
                >
                  <span className={`font-heading italic text-lg ${c.tone.includes('text-background') ? 'text-background' : 'text-text-primary'}`}>
                    {c.label}
                  </span>
                </motion.div>
              ))}
            </div>

            <a
              href="https://www.amazon.de/hz/wishlist/ls/3T6Q2EQZRCIJ5?ref_=wl_share"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-magnetic group inline-flex items-center gap-4 self-start bg-text-primary text-background pl-7 pr-3 py-3 rounded-full hover:bg-accent transition-colors duration-700"
              data-testid="favorites-cta"
            >
              <span className="font-body text-[0.78rem] uppercase tracking-[0.25em]">{t.favorites.cta}</span>
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-background text-text-primary group-hover:rotate-45 transition-transform duration-700">
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FavoritesSection;
